import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import {
  genTemplateBodyAction,
  genTemplateBodyDate,
  genTemplateBodyText,
} from "../../../../../components/utils/TableTemplateBodyGenerator";
import { confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Paginator } from "primereact/paginator";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useAPIGetSedangProses } from "../api/api-get-del-sedang-proses";
import extractUrlParams from "../api/extract-url-params";
import useIsMobile from "../../../../../hooks/useIsMobile";


const ListSedangProses = forwardRef((props, ref) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const { filterValue, onLoadingChange } = props;

  const {
    loading,
    paging,
    fetchListSedangProses,
    deleteData,
    listDataSedangProses,
    handleOnPage,
  } = useAPIGetSedangProses({ filterValue });

  useImperativeHandle(ref, () => ({
    reload: () => {
      fetchListSedangProses({ filterValue });
    },
  }));

  // useEffect section

  useEffect(() => {
    firstFetch();
  }, []);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  // other function section

  const firstFetch = async () => {
    const { page, take, filter, filterValue } = await extractUrlParams(
      location
    );
    if (filterValue) {
      fetchListSedangProses({ filterValue });
    }
  };

  // handle ui event section

  const handleOnClickDeleteRow = async (row) => {
    confirmDialog({
      message: "Menghapus form pemakaian mobil, lanjutkan?",
      header: "Konfirmasi Hapus",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteData({ id: row.id });
      },
    });
  };

  const handleOnClickEditBerangkat = async (row) => {
    const urlParams = new URLSearchParams(location.search);
    const key = urlParams.get("key");
    let url = `/transaksi/cek-keluar-masuk/berangkat/${row.id}`;
    if (key) {
      const encodedKey = encodeURIComponent(key);
      url += `?key=${encodedKey}`;
    }
    if (isMobile) {
      navigate(url);
    } else {
      // open new tab
      window.open(url, "_blank");
    }
  };

  const handleOnClickEditPulang = async (row) => {
    const urlParams = new URLSearchParams(location.search);
    const key = urlParams.get("key");
    let url = `/transaksi/cek-keluar-masuk/kembali/${row.id}`;
    if (key) {
      const encodedKey = encodeURIComponent(key);
      url += `?key=${encodedKey}`;
    }
    if (isMobile) {
      navigate(url);
    } else {
      // open new tab
      window.open(url, "_blank");
    }
  };

  // custom ui section

  const templateBodyAction = (row) =>
    genTemplateBodyAction({
      row,
      onDelete: (row) => {
        handleOnClickDeleteRow(row);
      },
    });

  const tanggalBodyTemplate = (row) =>
    genTemplateBodyDate({
      row,
      field: "tanggal_transaksi",
      format: "dd/MM/yyyy HH:mm",
    });

  const pemakaiBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_pemakai.nama" });
  const kendaraanBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_kendaraan.nomor_polisi" });
  const supirBodyTemplate = (row) => {
    return (
      <div className="col-start-start text-xs italic gap-2">
        <span>-{row.data_supir.nama}</span>
        {row.data_supir_pulang && <span>-{row.data_supir_pulang.nama}</span>}
      </div>
    );
  };

  const tujuanBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_tujuan.nama" });

  const berangkatBodyTemplate = (row) => {
    return (
      <div className="row-center-center gap-2">
        <Button
          icon="pi pi-sign-out"
          rounded
          severity="success"
          className="bg-emerald-400 max-w-[1.85rem] max-h-[1.85rem]"
          pt={{ icon: { className: "!text-xs" } }}
          onClick={() => handleOnClickEditBerangkat(row)}
        />
        {row.tanggal_berangkat && (
          <i className="pi pi-verified text-xl text-blue-500"></i>
        )}
      </div>
    );
  };

  const pulangBodyTemplate = (row) => {
    return (
      <div className="row-center-center gap-2">
        <Button
          icon="pi pi-sign-in"
          rounded
          severity="success"
          className="bg-emerald-400 max-w-[1.85rem] max-h-[1.85rem]"
          pt={{ icon: { className: "!text-xs" } }}
          onClick={() => handleOnClickEditPulang(row)}
        />
        {row.tanggal_pulang && (
          <i className="pi pi-verified text-xl text-blue-500"></i>
        )}
      </div>
    );
  };

  const deskripsiSmallSizeBodyTemplate = (row) => {
    const {
      data_kendaraan,
      data_pemakai,
      data_supir,
      data_supir_pulang,
      data_tujuan,
      tanggal_transaksi,
      no,
    } = row;

    return (
      <div className="col-start-start py-2">
        <div className="row-start-center gap-2 w-full">
          <span className="text-slate-400 text-sm font-semibold"># {no}, </span>
          <span className="text-slate-400 text-sm font-semibold italic">
            {format(new Date(tanggal_transaksi), "dd/MM/yyyy HH:mm")}
          </span>
        </div>

        <div className="row-start-center gap-2 pt-3">
          <span>{data_kendaraan?.jenis_mobil},</span>
          <span>{data_kendaraan?.nomor_polisi}</span>
        </div>
        <div className="col-start-start pt-3">
          <div className="row-start-center text-sm italic gap-2">
            <span className="w-[4.5rem] text-right"> Pemakai: </span>
            <span className="font-bold">{data_pemakai?.nama}</span>
          </div>
          <div className="row-start-center text-sm italic gap-2">
            <span className="w-[4.5rem] text-right"> Supir Berangkat: </span>
            <span className="font-bold">{data_supir?.nama}</span>
          </div>
          <div className="row-start-center text-sm italic gap-2">
            <span className="w-[4.5rem] text-right"> Supir Pulang: </span>
            <span className="font-bold">{data_supir_pulang?.nama}</span>
          </div>
          <div className="row-start-center text-sm italic gap-2">
            <span className="w-[4.5rem] text-right"> Tujuan: </span>
            <span className="font-bold">{data_tujuan?.nama}</span>
          </div>
        </div>

        <div className="row-around-start w-full pt-4 ">
          <div className="row-start-start w-full  gap-4">
            <div className="row-start-center gap-1">
              <Button
                icon="pi pi-sign-out"
                rounded
                severity="success"
                className="bg-emerald-400 max-w-[1.85rem] max-h-[1.85rem]"
                pt={{ icon: { className: "!text-xs" } }}
                onClick={() => handleOnClickEditBerangkat(row)}
              />
              {row.tanggal_berangkat && (
                <i className="pi pi-verified text-xl text-blue-500"></i>
              )}
              Pergi
            </div>
            <div className="row-start-center gap-1">
              <Button
                icon="pi pi-sign-in"
                rounded
                severity="success"
                className="bg-emerald-400 max-w-[1.85rem] max-h-[1.85rem]"
                pt={{ icon: { className: "!text-xs" } }}
                onClick={() => handleOnClickEditPulang(row)}
              />
              {row.tanggal_pulang && (
                <i className="pi pi-verified text-xl text-blue-500"></i>
              )}
              Pulang
            </div>
          </div>
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            className="bg-red-400 border-red-400 max-w-[1.85rem] max-h-[1.85rem]"
            onClick={() => handleOnClickDeleteRow(row)}
            pt={{ icon: { className: "!text-xs" } }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`elevated-card row-start-start w-100  wrap gap-4`}>
      <span className="text-sm font-semibold text-zinc-700">
        SEDANG DI PROSES
      </span>

      <div className={`col-center-center gap-2 w-100`}>
        {!isMobile && (
          <>
            <DataTable
              dataKey="id"
              className="w-100"
              loading={loading}
              showGridlines
              stripedRows
              editMode="cell"
              value={listDataSedangProses}
            >
              <Column
                field="no"
                header="#"
                headerClassName="w-[4rem]"
                className="text-right"
                alignHeader={"right"}
              />
              <Column headerClassName="w-[4rem]" body={templateBodyAction} />
              <Column header="Tanggal" body={tanggalBodyTemplate} />
              <Column header="Kendaraan" body={kendaraanBodyTemplate} />
              <Column header="Pemakai" body={pemakaiBodyTemplate} />
              <Column header="Supir" body={supirBodyTemplate} />
              <Column header="Tujuan" body={tujuanBodyTemplate} />
              <Column
                header="Berangkat"
                body={berangkatBodyTemplate}
                alignHeader={"center"}
              />
              <Column
                header="Kembali"
                body={pulangBodyTemplate}
                alignHeader={"center"}
              />
            </DataTable>
            <Paginator
              first={parseInt(paging?.take) * (paging?.page - 1)}
              rows={parseInt(paging?.take)}
              totalRecords={parseInt(paging?.total)}
              rowsPerPageOptions={[5, 10, 20, 50]}
              onPageChange={handleOnPage}
            />
          </>
        )}

        {isMobile && (
          <>
            <DataTable
              dataKey="id"
              className="w-100"
              loading={loading}
              showGridlines
              stripedRows
              editMode="cell"
              value={listDataSedangProses}
            >
              <Column
                header="Deskripsi"
                align={"center"}
                body={deskripsiSmallSizeBodyTemplate}
              />
            </DataTable>
            <Paginator
              first={parseInt(paging.take) * (paging.page - 1)}
              rows={parseInt(paging.take)}
              totalRecords={parseInt(paging.total)}
              rowsPerPageOptions={[5, 10, 20, 50]}
              template={{
                layout: "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown",
              }}
              onPageChange={handleOnPage}
              pt={{
                prevPageButton: {
                  className:
                    "bg-white-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
                },
                nextPageButton: {
                  className:
                    "bg-white-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
                },
                current: {
                  className: "bg-white-100 !m-0 max-h-[22px] text-sm",
                },
                rowsPerPageDropdown: {
                  className: "bg-white-100 !m-0 max-h-[22px]",
                },
              }}
            />
          </>
        )}


      </div>
    </div>
  );
});

export default ListSedangProses;
